// src/hooks/init/useRetailerNavigation.js
import { useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import * as storage from '../../browserStorage';
import { storageItemName } from '../../config';
import { getExistingCheckout, consoleDebug } from '../../util';

export const useRetailerNavigation = ({
    isInitialized,
    retailerId,
    retailers,
    isSingleRetailer,
    getRetailerBySlug,
    allRetailersConfig,
}) => {
    const params = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Handle navigation redirects
    useEffect(() => {
        if (!isInitialized || !retailerId || !retailers[retailerId]) return;

        const menuSlug = retailers[retailerId]?.menuSlug;
        if (!menuSlug) return;

        // Preserve query params
        const searchParams = new URLSearchParams(location.search);
        const preservedParams = new URLSearchParams();
        ['search', 'thcMin', 'thcMax', 'menuType'].forEach(key => {
            if (searchParams.has(key)) {
                preservedParams.set(key, searchParams.get(key));
            }
        });
        const queryString = preservedParams.toString();
        const queryStringWithQuestionMark = queryString ? `?${queryString}` : '';

        // Single retailer redirect
        if (isSingleRetailer && params?.viewName === 'shop' && !params?.locationSlug) {
            navigate(`/shop/${menuSlug}/${queryStringWithQuestionMark}`, { replace: true });
            return;
        }

        // Handle direct category navigation WITHOUT a retailer slug
        const categoryTypes = ['brand', 'category', 'tags', 'effect', 'specials', 'subcategory'];
        if (params?.viewName === 'shop' && categoryTypes.includes(params?.locationSlug) && !params?.categoryType) {
            const categoryType = params.locationSlug;
            const categoryName = params.categoryType || '';

            let redirectPath = `/shop/${menuSlug}/${categoryType}/`;
            if (categoryName) {
                redirectPath += `${categoryName}/`;
            }
            redirectPath += queryStringWithQuestionMark;

            navigate(redirectPath, {
                state: { setFilters: true },
                replace: true
            });
            return;
        }

        // Handle the case where we have /shop/category/flower/something
        if (params?.viewName === 'shop' && categoryTypes.includes(params?.locationSlug) && params?.categoryType) {
            const retailerMatch = getRetailerBySlug(params.locationSlug);
            if (!retailerMatch) {
                const categoryType = params.locationSlug;
                const categoryName = params.categoryType;
                const additionalParam = params.categoryName || '';

                let redirectPath = `/shop/${menuSlug}/${categoryType}/${categoryName}/`;
                if (additionalParam) {
                    redirectPath += `${additionalParam}/`;
                }
                redirectPath += queryStringWithQuestionMark;

                navigate(redirectPath, {
                    state: { setFilters: true },
                    replace: true
                });
                return;
            }
        }

        // Handle base /shop/ redirects
        if (params?.viewName === 'shop' && (!params?.locationSlug || params.locationSlug === 'undefined')) {
            navigate(`/shop/${menuSlug}/${queryStringWithQuestionMark}`, {
                state: { setFilters: false },
                replace: true
            });
        }
    }, [isInitialized, retailerId, retailers, params, location.search, navigate, isSingleRetailer, getRetailerBySlug]);

    // Update retailer with navigation
    const updateRetailerId = useCallback((newRetailerId, setRetailerId) => {
        consoleDebug('🔄 updateRetailerId called:', {
            current: retailerId,
            new: newRetailerId
        });

        if (newRetailerId === retailerId) return;

        setRetailerId(newRetailerId);

        // Clear cart if retailer changed
        const checkout = getExistingCheckout();
        if (checkout && checkout.retailerId !== newRetailerId) {
            storage.removeItem(storageItemName);
            consoleDebug('🗑️ Cleared cart for retailer change');
        }

        // Handle redirects
        const retailer = retailers[newRetailerId];
        if (retailer && allRetailersConfig) {
            setTimeout(() => {
                if (allRetailersConfig.locationMenuRedirect && retailer.menuSlug) {
                    if (!window.location.pathname.startsWith(`/shop/${retailer.menuSlug}`)) {
                        consoleDebug('🔄 Redirecting to menu:', `/shop/${retailer.menuSlug}/`);
                        window.location.href = `/shop/${retailer.menuSlug}/`;
                    }
                } else if (allRetailersConfig.locationHomepageRedirect && retailer.locationHomepage) {
                    consoleDebug('🔄 Redirecting to homepage:', retailer.locationHomepage);
                    window.location.href = retailer.locationHomepage;
                }
            }, 300);
        }
    }, [retailerId, retailers, allRetailersConfig]);

    return {
        updateRetailerId
    };
};
